/**
 * The AI suggestion control.
 *
 * One component wherever AI can help, so the flow is identical everywhere:
 * ask · review · Apply or Discard. It **never** writes to the field it sits
 * next to. `onApply` is called only when the user presses Apply, and the change
 * then goes through the ordinary save path like any other edit.
 *
 * When no provider is configured this renders nothing at all — not a disabled
 * button, not an upsell. An installation running without AI should not be
 * covered in reminders that it could be running with it.
 */
import { useState } from 'react';
import {
  ApiError,
  endpoints,
  type AIFunctionId,
  type AIStatus,
  type AISuggestion,
  type GuardrailFinding,
} from '../../lib/api';
import { Alert, Button } from '../../components/ui';

export function AIAssist({
  proposalId,
  status,
  functions,
  currentText,
  onApply,
  disabled,
  label = 'AI assistance',
}: {
  proposalId: string;
  status: AIStatus | null;
  /** Which functions make sense here, in the order to offer them. */
  functions: AIFunctionId[];
  /** The text being worked on, for functions that rewrite. */
  currentText?: string;
  onApply(text: string): void;
  disabled?: boolean;
  label?: string;
}) {
  const [busy, setBusy] = useState<AIFunctionId | null>(null);
  const [suggestion, setSuggestion] = useState<AISuggestion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [guardrail, setGuardrail] = useState<GuardrailFinding[] | null>(null);

  // No provider, no affordance.
  if (!status?.enabled) return null;

  const available = status.functions.filter((entry) => functions.includes(entry.id));
  if (available.length === 0) return null;

  const request = async (functionId: AIFunctionId) => {
    setBusy(functionId);
    setError(null);
    setGuardrail(null);
    setSuggestion(null);
    try {
      const result = await endpoints.aiSuggest(proposalId, functionId, currentText);
      setSuggestion(result.suggestion);
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught.message);
        const details = caught.details as { guardrail?: GuardrailFinding[] } | undefined;
        if (details?.guardrail) setGuardrail(details.guardrail);
      } else {
        setError('The suggestion could not be produced.');
      }
    } finally {
      setBusy(null);
    }
  };

  const discard = () => {
    if (suggestion) void endpoints.aiOutcome(proposalId, suggestion.functionId, false, suggestion);
    setSuggestion(null);
  };

  const apply = () => {
    if (!suggestion) return;
    onApply(suggestion.text);
    void endpoints.aiOutcome(proposalId, suggestion.functionId, true, suggestion);
    setSuggestion(null);
  };

  return (
    <div className="rounded-md border border-rule bg-tint px-3.5 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[12px] font-medium text-ink">{label}</span>
        <span className="text-[11px] text-muted">
          {status.provider} · {status.model}
        </span>
      </div>

      <div className="mt-2 flex flex-wrap gap-2">
        {available.map((entry) => (
          <Button
            key={entry.id}
            variant="secondary"
            size="sm"
            loading={busy === entry.id}
            disabled={
              disabled || busy !== null || (entry.requiresCurrentText && !currentText?.trim())
            }
            title={`Sends: ${entry.sends.join(', ')}`}
            onClick={() => void request(entry.id)}
          >
            {entry.label}
          </Button>
        ))}
      </div>

      <p className="mt-2 text-[11px] text-muted">
        Suggestions are shown for you to review. Nothing is changed until you apply it.
      </p>

      {error && (
        <div className="mt-3">
          <Alert tone={guardrail ? 'warning' : 'error'} title={guardrail ? 'Suggestion discarded' : undefined}>
            {error}
            {guardrail && (
              <ul className="mt-1.5 list-disc pl-4 text-[12px]">
                {guardrail.map((finding) => (
                  <li key={finding.kind}>
                    {finding.explanation}: “{finding.match}”
                  </li>
                ))}
              </ul>
            )}
          </Alert>
        </div>
      )}

      {suggestion && (
        <div className="mt-3">
          <Alert tone="info" title="AI suggestion">
            {suggestion.needsReview && (
              <p className="mb-2 text-[12px] font-medium text-amber-900">
                The assistant flagged a gap rather than filling it. Read the [REVIEW] note
                below and supply the missing detail yourself.
              </p>
            )}
            <p className="whitespace-pre-wrap text-[13px]">{suggestion.text}</p>
            <p className="mt-2 text-[11px] text-muted">
              Sent: {suggestion.contextSent.length} field
              {suggestion.contextSent.length === 1 ? '' : 's'} · {suggestion.provider}/
              {suggestion.model}
            </p>
            <div className="mt-3 flex gap-2">
              <Button size="sm" onClick={apply}>
                Apply
              </Button>
              <Button variant="secondary" size="sm" onClick={discard}>
                Discard
              </Button>
            </div>
          </Alert>
        </div>
      )}
    </div>
  );
}
